import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import { assignNurseInRoom } from '@/graph/patients';
import { gql, useMutation, useSubscription } from '@apollo/client';
import StaffSlot from '@/components/pages/kiosk/PACUv2/StaffSlot';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';

export interface ConfirmationDialogRawProps {
  id: string;
  keepMounted: boolean;
  patientId: number;
  roomId: number;
  open: boolean;
  onClose: (value?: string) => void;
  /** Staff specialty filter; defaults to PACU for backward compatibility */
  primarySpecialty?: 'PACU' | 'PRE_OP' | 'OR' | 'SEDATION';
}

function SelectPatientDialog(props: ConfirmationDialogRawProps) {
  const { onClose, open, patientId, roomId, primarySpecialty = 'PACU', ...other } = props;
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = React.useState('');
  const radioGroupRef = React.useRef<HTMLElement>(null);

  const { data, loading } = useSubscription(
    gql`
      subscription staffByPrimarySpecialty($primarySpecialty: StaffPrimarySpecialty) {
        staffMembers: staffByPrimarySpecialty(primarySpecialty: $primarySpecialty) {
          id
          staffId
          name
        }
      }
    `,
    {
      variables: {
        primarySpecialty,
      },
      skip: !open,
    }
  );

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const [assignNurseMutation] = useMutation(assignNurseInRoom);

  const handleChange = async (staffId: string) => {
    try {
      await assignNurseMutation({
        variables: {
          id: patientId,
          staffId: staffId,
          roomId: roomId,
        },
      });

      onClose();
    } catch (e) {
      console.error(e);
    }
  };

  const filteredStaffMembers = React.useMemo(() => {
    if (!data?.staffMembers) return [];
    return data.staffMembers.filter(staff => staff.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [data, searchTerm]);

  const columnCount = React.useMemo(() => {
    const count = filteredStaffMembers.length;
    if (count > 30) return 3;
    if (count > 15) return 2;
    return 1;
  }, [filteredStaffMembers.length]);

  return (
    <Dialog
      maxWidth="md"
      onEntering={handleEntering}
      aria-labelledby="confirmation-dialog-title"
      open={open}
      onClose={handleCancel}
      classes={{
        paper: classes.dialogPaper,
      }}
      {...other}
    >
      <DialogTitle id="confirmation-dialog-title">Assign nurse to patient</DialogTitle>
      <DialogContent dividers>
        <Box className={classes.searchContainer}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search nurses..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </Box>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <Box className={classes.staffList} style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}>
            {filteredStaffMembers.map(staffMember => (
              <StaffSlot
                key={staffMember.id}
                name={staffMember.name}
                onClick={() => handleChange(staffMember.staffId)}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button autoFocus onClick={handleCancel} color="primary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: '80%',
    maxHeight: '80vh',
  },
  searchContainer: {
    marginBottom: theme.spacing(2),
  },
  staffList: {
    display: 'grid',
    gap: theme.spacing(1),
    overflowY: 'auto',
    maxHeight: '60vh',
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: '3px',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
}));

export default SelectPatientDialog;
